import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { API } from "@/config/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { FileText, CheckCircle } from "lucide-react";

const NDAPage = () => {
  const navigate = useNavigate();
  const { signNda, ndaSigned, accessGranted } = useAuth();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    signature: "",
    agreed: false,
  });
  const [loading, setLoading] = useState(false);

  // Redirect checks
  React.useEffect(() => {
    if (!accessGranted) {
      navigate("/access");
    } else if (ndaSigned) {
      navigate("/register");
    }
  }, [accessGranted, ndaSigned, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreed) {
      toast.error("You must agree to the terms");
      return;
    }
    
    if (formData.signature !== formData.full_name) {
      toast.error("Signature must match your full name");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/nda/submit`, formData);
      signNda();
      toast.success("NDA submitted successfully");
      navigate("/register");
    } catch (err) {
      toast.error("Failed to submit NDA");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen py-8 px-4"
      style={{ background: "#050505" }}
      data-testid="nda-page"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_hoop-invite/artifacts/r8lcaocz_Board2Boston%20favicon.png"
            alt="Board2Boston"
            style={{ height: '140px' }}
            className="mx-auto mb-4"
          />
          <h1 className="font-bebas text-4xl md:text-5xl tracking-wider text-white">
            NON-DISCLOSURE AGREEMENT
          </h1>
          <p className="text-[#A1A1AA] font-ubuntu mt-2">
            Please review and sign the agreement to continue
          </p>
        </div>

        {/* Reduced width card - 70% of original (30% reduction) */}
        <Card className="bg-[#0A0A0A] border border-white/10 mx-auto" style={{ maxWidth: '70%' }}>
          <CardHeader className="border-b border-white/10">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-[#7a6200]" />
              <CardTitle className="font-bebas text-2xl tracking-wider text-white">
                MEMBERSHIP AGREEMENT & TERMS
              </CardTitle>
            </div>
          </CardHeader>

          <CardContent className="p-6">
            {/* NDA Content */}
            <ScrollArea className="h-64 mb-6 p-4 bg-black/30 rounded border border-white/5">
              <div className="text-[#A1A1AA] font-ubuntu text-sm space-y-4 pr-4">
                <p className="font-bold text-white">BOARD2BOSTON™ MEMBERSHIP AGREEMENT AND NON-DISCLOSURE AGREEMENT</p>
                
                <p>This Agreement is entered into between Board2Boston™ ("B2B™", "Company") and the undersigned Member ("Member", "You").</p>
                
                <p className="font-bold text-white mt-4">1. MEMBERSHIP TERMS</p>
                <p>By becoming a member of Board2Boston™, you agree to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Provide accurate and truthful information during registration</li>
                  <li>Complete identity verification as required</li>
                  <li>Maintain the confidentiality of your account credentials</li>
                  <li>Comply with all community guidelines and rules</li>
                  <li>Treat other members with respect and courtesy</li>
                </ul>

                <p className="font-bold text-white mt-4">2. CONFIDENTIALITY</p>
                <p>Member agrees to keep confidential:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Proprietary gaming strategies shared within the community</li>
                  <li>Personal information of other members</li>
                  <li>Venue partner locations not publicly disclosed</li>
                  <li>Internal tournament structures and prize information</li>
                  <li>Any business information shared by B2B™</li>
                </ul>

                <p className="font-bold text-white mt-4">3. CODE OF CONDUCT</p>
                <p>Members agree to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>No cheating, collusion, or unfair play</li>
                  <li>No harassment, discrimination, or abusive behavior</li>
                  <li>Respect host rules and venue policies</li>
                  <li>Report any violations to B2B™ administration</li>
                </ul>

                <p className="font-bold text-white mt-4">4. LIABILITY</p>
                <p>Board2Boston™ is not liable for any disputes, injuries, or losses occurring at events. Members participate at their own risk and agree to hold harmless B2B™ and its partners.</p>

                <p className="font-bold text-white mt-4">5. TERMINATION</p>
                <p>B2B™ reserves the right to terminate membership for any violation of these terms without refund of membership fees.</p>

                <p className="font-bold text-white mt-4">6. AGREEMENT</p>
                <p>By signing below, you acknowledge that you have read, understood, and agree to be bound by all terms and conditions of this Agreement.</p>
              </div>
            </ScrollArea>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                    Full Legal Name *
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="input-dark h-12"
                    required
                    data-testid="nda-fullname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-dark h-12"
                    required
                    data-testid="nda-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  Digital Signature (Type your full name exactly as above) *
                </label>
                <Input
                  type="text"
                  placeholder="Type your full name as signature"
                  value={formData.signature}
                  onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                  className="input-dark h-12 italic"
                  required
                  data-testid="nda-signature"
                />
              </div>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, agreed: !formData.agreed })}
                className="flex items-start gap-3 p-4 bg-black/30 rounded border border-white/5 w-full text-left hover:border-[#7a6200]/30 transition-colors"
                data-testid="nda-checkbox"
              >
                <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  formData.agreed ? "bg-[#7a6200] border-[#7a6200]" : "border-[#7a6200]"
                }`}>
                  {formData.agreed && (
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
                <span className="text-sm font-ubuntu text-[#A1A1AA]">
                  I have read and agree to the Board2Boston™ Membership Agreement and Non-Disclosure Agreement. I understand that violation of these terms may result in termination of membership.
                </span>
              </button>

              <Button
                type="submit"
                className="btn-gold w-full h-14 text-lg"
                disabled={loading || !formData.agreed || !formData.full_name || !formData.email || !formData.signature}
                data-testid="nda-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    SUBMITTING...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    SIGN & CONTINUE
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NDAPage;
